import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Download, Building2, BarChart3, Users, ClipboardCheck, Trophy, GitCompare, Loader2, X } from "lucide-react";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const API = process.env.REACT_APP_BACKEND_URL;

async function downloadFile(url, fallbackName) {
  const res = await fetch(`${API}/api${url}`, { credentials: "include" });
  if (!res.ok) {
    const txt = await res.text();
    let detail = "Errore generazione";
    try { detail = JSON.parse(txt).detail || detail; } catch {}
    throw new Error(detail);
  }
  const blob = await res.blob();
  // try to extract filename from Content-Disposition
  let filename = fallbackName;
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const m = cd.match(/filename=([^;]+)/i);
    if (m) filename = m[1].trim().replace(/["']/g, "");
  }
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

const REPORT_CARDS = [
  { key: "hotel-monthly", icon: Building2, title: "Report mensile hotel", desc: "Riepilogo completo di un hotel per un mese: controlli, ispezione, HK, alert, relazione mensile." },
  { key: "corporate-quarterly", icon: BarChart3, title: "Trimestrale corporate", desc: "Tutti gli hotel del cluster, confronto KPI sul trimestre.", roles: ["super_admin", "capo_area"] },
  { key: "confronto", icon: GitCompare, title: "Confronto strutture", desc: "Side-by-side tra hotel selezionati per il mese scelto." },
  { key: "classifica", icon: Trophy, title: "Classifica corporate", desc: "Top/worst hotel per qualità, produttività HK, completion controlli." },
  { key: "rm", icon: Users, title: "Report Resident Manager", desc: "Performance individuale RM: task, qualità, relazione." },
  { key: "ispezione", icon: ClipboardCheck, title: "Report ispezione", desc: "Dettaglio completo di un ispezione: 11 categorie, punteggi, foto, firma." },
];

export default function Report() {
  const { user } = useAuth();
  const [open, setOpen] = useState(null);

  const visible = REPORT_CARDS.filter((c) => !c.roles || c.roles.includes(user?.role));

  return (
    <div className="space-y-6 ih-anim-in" data-testid="report-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><FileText className="h-3 w-3" /> Esportazioni</div>
        <h1 className="font-display text-2xl tracking-tight font-semibold">Report PDF &amp; Excel</h1>
        <p className="text-sm text-muted-foreground mt-1">Genera e scarica report brandizzati IH AuditFlow.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((c) => (
          <button key={c.key} onClick={() => setOpen(c.key)} data-testid={`card-${c.key}`} className="ih-glass rounded-2xl p-6 ih-card-hover text-left flex flex-col gap-3 group">
            <div className="h-12 w-12 rounded-xl bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center">
              <c.icon className="h-6 w-6 text-lavender-600 dark:text-lavender-300" />
            </div>
            <div>
              <h2 className="font-display text-lg tracking-tight">{c.title}</h2>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
            </div>
            <span className="text-[11px] text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"><Download className="h-3 w-3" /> Genera report</span>
          </button>
        ))}
      </div>
      <div className="flex-1" />

      {open && <ReportModal type={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ReportModal({ type, onClose }) {
  const today = new Date();
  const [form, setForm] = useState({
    hotel_id: "", hotel_ids: [], anno: today.getFullYear(), mese: today.getMonth() + 1,
    trimestre: Math.ceil((today.getMonth() + 1) / 3), rm_id: "", audit_id: "", format: "pdf",
  });
  const [hotels, setHotels] = useState([]);
  const [rms, setRms] = useState([]);
  const [ispezioni, setAudits] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/hotels").then((r) => {
      setHotels(r.data || []);
      if (r.data?.length && !form.hotel_id) setForm((f) => ({ ...f, hotel_id: r.data[0].id, hotel_ids: [r.data[0].id] }));
    });
    if (type === "rm") {
      api.get("/users?role=resident_manager").then((r) => {
        setRms(r.data || []);
        if (r.data?.length) setForm((f) => ({ ...f, rm_id: r.data[0].id }));
      }).catch(() => {
        // fallback: rm-performance gives RMs in scope
        api.get("/rm-performance?periodo=30").then((r) => {
          const list = (r.data || []).map((x) => x.resident_manager);
          setRms(list);
          if (list.length) setForm((f) => ({ ...f, rm_id: list[0].id }));
        });
      });
    }
    if (type === "ispezione") {
      api.get("/audits?stato=completato").then((r) => {
        setAudits(r.data || []);
        if (r.data?.length) setForm((f) => ({ ...f, audit_id: r.data[0].id }));
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const buildUrl = () => {
    const f = form.format;
    if (type === "hotel-monthly") return `/reports-export/hotel-monthly?hotel_id=${form.hotel_id}&anno=${form.anno}&mese=${form.mese}&format=${f}`;
    if (type === "corporate-quarterly") return `/reports-export/corporate-quarterly?anno=${form.anno}&trimestre=${form.trimestre}&format=${f}`;
    if (type === "confronto") return `/reports-export/confronto-strutture?hotel_ids=${form.hotel_ids.join(",")}&anno=${form.anno}&mese=${form.mese}&format=${f}`;
    if (type === "classifica") return `/reports-export/classifica?anno=${form.anno}&mese=${form.mese}&format=${f}`;
    if (type === "rm") return `/reports-export/resident-manager?rm_id=${form.rm_id}&anno=${form.anno}&mese=${form.mese}&format=${f}`;
    if (type === "audit") return `/reports-export/audit/${form.audit_id}?format=${f}`;
    return "";
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await downloadFile(buildUrl(), `ih-auditflow-${type}.${form.format}`);
      toast.success("Report scaricato");
      onClose();
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    } finally { setGenerating(false); }
  };

  const toggleHotel = (id) => {
    setForm((f) => ({ ...f, hotel_ids: f.hotel_ids.includes(id) ? f.hotel_ids.filter((x) => x !== id) : [...f.hotel_ids, id] }));
  };

  const title = REPORT_CARDS.find((c) => c.key === type)?.title || "";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background rounded-2xl border border-border w-full max-w-lg p-6" data-testid={`report-modal-${type}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">{title}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3">
          {type === "hotel-monthly" && (
            <>
              <select data-testid="r-hotel" value={form.hotel_id} onChange={(e) => setForm({ ...form, hotel_id: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {hotels.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
              </select>
            </>
          )}
          {type === "confronto" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hotel da confrontare</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {hotels.map((h) => (
                  <button key={h.id} onClick={() => toggleHotel(h.id)} className={`px-2.5 py-1 rounded-full text-[11px] border ${form.hotel_ids.includes(h.id) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{h.nome}</button>
                ))}
              </div>
            </div>
          )}
          {(type === "hotel-monthly" || type === "confronto" || type === "classifica" || type === "rm") && (
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="r-mese" value={form.mese} onChange={(e) => setForm({ ...form, mese: parseInt(e.target.value) })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <input data-testid="r-anno" type="number" value={form.anno} onChange={(e) => setForm({ ...form, anno: parseInt(e.target.value) })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
          )}
          {type === "corporate-quarterly" && (
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="r-trim" value={form.trimestre} onChange={(e) => setForm({ ...form, trimestre: parseInt(e.target.value) })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {[1, 2, 3, 4].map((t) => <option key={t} value={t}>Trimestre {t}</option>)}
              </select>
              <input type="number" value={form.anno} onChange={(e) => setForm({ ...form, anno: parseInt(e.target.value) })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
          )}
          {type === "rm" && (
            <select data-testid="r-rm" value={form.rm_id} onChange={(e) => setForm({ ...form, rm_id: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
              {rms.map((r) => <option key={r.id} value={r.id}>{r.nome} {r.cognome}</option>)}
            </select>
          )}
          {type === "ispezione" && (
            <select data-testid="r-audit" value={form.audit_id} onChange={(e) => setForm({ ...form, audit_id: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
              {ispezioni.map((a) => <option key={a.id} value={a.id}>{a.data_creazione?.slice(0, 10)} · {a.punteggio_totale}/100 · {a.valutazione_complessiva}</option>)}
            </select>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Formato</label>
            <div className="mt-1 flex gap-2">
              {["pdf", "xlsx"].map((f) => (
                <button key={f} onClick={() => setForm({ ...form, format: f })} data-testid={`r-fmt-${f}`} className={`flex-1 px-4 py-2 rounded-full text-sm border ${form.format === f ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{f.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-full border border-border text-sm">Annulla</button>
          <button onClick={generate} disabled={generating} data-testid="r-generate" className="flex-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {generating ? "Generazione…" : "Genera & Scarica"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable export button for hotel/ispezione pages
export function ExportButton({ url, filename, label = "Esporta" }) {
  const [loading, setLoading] = useState(false);
  const click = async () => {
    setLoading(true);
    try {
      await downloadFile(url, filename);
      toast.success("Report scaricato");
    } catch (err) {
      toast.error(err.message || "Errore");
    } finally { setLoading(false); }
  };
  return (
    <button onClick={click} disabled={loading} data-testid="export-btn" className="px-3 py-1.5 rounded-full border border-border text-xs font-medium flex items-center gap-1.5 hover:bg-accent/40 disabled:opacity-50">
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}
